import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";

/**
 * TODO: Remove debug console.debug statements once this service is confirmed working properly.
 * Date added: September 12, 2025
 *
 * Debug statements to remove:
 * - checkAndResetGridState method console.debug calls
 * - getStoredGridVersion method console.debug calls
 * - updateGridVersion method console.debug calls
 */

export interface GridStateVersion {
  version: string;
  gridId: string;
  lastUpdated: Date;
}

@Injectable({
  providedIn: "root",
})
export class GridStateManagerService {
  private readonly GRID_STATE_VERSION_KEY = "identity-grid-state-versions";
  private readonly GRID_STATE_PREFIX = "identity-grid-";

  // Use environment version if available, otherwise fallback to manual version
  // This will automatically use your app version when properly configured
  private currentGridVersion = environment.version?.version || "2.2.0";

  /**
   * Checks if grid state should be reset based on version mismatch
   * Call this in component ngOnInit before grid initialization
   */
  public checkAndResetGridState(gridId: string): void {
    const currentVersion = this.getCurrentAppVersion();
    const storedVersion = this.getStoredGridVersion(gridId);

    console.debug(`Version check for ${gridId}: current=${currentVersion}, stored=${storedVersion}`);

    // Only reset if there's a stored version AND it doesn't match current version
    // Don't reset grids that have never been versioned (storedVersion is null)
    if (storedVersion && storedVersion !== currentVersion) {
      console.debug(`Grid state version mismatch for ${gridId}. Resetting...`);
      this.resetGridState(gridId);
      this.setGridVersion(gridId, currentVersion);
    } else if (!storedVersion) {
      console.debug(`No stored version for ${gridId}. Setting initial version.`);
      this.setGridVersion(gridId, currentVersion);
    } else {
      console.debug(`Versions match for ${gridId}. No reset needed.`);
    }
  }

  /**
   * Force reset all grids (useful for testing or manual reset)
   */
  public forceResetAllGrids(): void {
    this.resetAllGridStates();
  }

  /**
   * Get diagnostic information about stored grid states
   */
  public getGridStateDiagnostics(): { key: string; size: number }[] {
    const diagnostics: { key: string; size: number }[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key !== this.GRID_STATE_VERSION_KEY && this.isGridRelatedKey(key)) {
        const value = localStorage.getItem(key);
        const size = value ? new Blob([value]).size : 0;
        diagnostics.push({ key, size });
      }
    }

    return diagnostics.sort((a, b) => b.size - a.size);
  }

  /**
   * Check if app version has changed since last grid state save
   */
  public hasAppVersionChanged(): boolean {
    const currentVersion = this.getCurrentAppVersion();
    const lastKnownVersion = localStorage.getItem("identity-app-last-version");

    if (!lastKnownVersion || lastKnownVersion !== currentVersion) {
      localStorage.setItem("identity-app-last-version", currentVersion);
      return true;
    }

    return false;
  }

  /**
   * Reset all grid states in the application
   * This includes both SyncFusion grids and other table states
   */
  public resetAllGridStates(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isGridRelatedKey(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(this.GRID_STATE_VERSION_KEY);
  }

  /**
   * Check if a localStorage key is grid-related
   */
  private isGridRelatedKey(key: string): boolean {
    return (
      key.startsWith(this.GRID_STATE_PREFIX) ||
      key.includes("ej2Grid") ||
      key.includes("syncfusion") ||
      key.includes("sf-grid-") ||
      key.includes("table-state-") ||
      key.includes("ng-mcs-table") ||
      key.startsWith("identity-table-") ||
      key.startsWith("identity-grid-")
    );
  }

  /**
   * Manually reset a specific grid's state
   */
  public resetGridState(gridId: string): void {
    const keysToRemove: string[] = [];

    // Define specific patterns that should be removed for this grid
    const gridPatterns = [
      `ej2Grid-${gridId}`,
      `sf-grid-${gridId}`,
      `syncfusion-grid-${gridId}`,
      `identity-grid-${gridId}`,
      `identity-table-${gridId}`,
      `table-state-${gridId}`,
      `ng-mcs-table-${gridId}`,
    ];

    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if key matches any of the specific patterns for this grid
        const matchesPattern = gridPatterns.some((pattern) => key === pattern);

        // Also check for keys that contain the gridId and are grid-related
        const isGridRelated = this.isGridRelatedKey(key);
        const containsGridId = key.includes(gridId) && isGridRelated;

        if (matchesPattern || containsGridId) {
          keysToRemove.push(key);
        }
      }
    }

    // Remove all identified keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Reset specific SyncFusion grid types
   */
  public resetSyncFusionGridStates(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes("ej2Grid") ||
          key.includes("syncfusion") ||
          key.includes("sf-grid-") ||
          key.startsWith("identity-grid-"))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  /**
   * Update the grid version when making breaking changes to grid configuration
   * Call this method when you make significant changes to grid columns, filters, etc.
   */
  public updateGridVersion(newVersion: string): void {
    this.currentGridVersion = newVersion;
    console.debug(`Updated grid version to: ${newVersion}`);
  }

  /**
   * Get current application version from environment or package.json
   */
  private getCurrentAppVersion(): string {
    // Check if environment version is available and not a placeholder
    const envVersion = environment.version?.version;
    if (envVersion && !envVersion.includes("POST_BUILD_ENTERS_VERSION")) {
      return envVersion;
    }
    // Fall back to hardcoded version for tests/development
    return "2.2.0";
  }

  /**
   * Get stored version for a specific grid
   */
  private getStoredGridVersion(gridId: string): string | null {
    try {
      const versions = this.getStoredVersions();
      console.debug(`All stored versions:`, versions);
      const gridVersion = versions.find((v) => v.gridId === gridId);
      console.debug(`Found version for ${gridId}:`, gridVersion);
      return gridVersion?.version || null;
    } catch (error) {
      console.error("Error getting stored grid version:", error);
      return null;
    }
  }

  /**
   * Get all stored versions
   */
  private getStoredVersions(): GridStateVersion[] {
    try {
      const stored = localStorage.getItem(this.GRID_STATE_VERSION_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error parsing stored versions:", error);
      return [];
    }
  }

  /**
   * Set version for a specific grid
   */
  private setGridVersion(gridId: string, version: string): void {
    try {
      const versions = this.getStoredVersions();
      const existingIndex = versions.findIndex((v) => v.gridId === gridId);

      const newVersion: GridStateVersion = {
        gridId,
        version,
        lastUpdated: new Date(),
      };

      if (existingIndex >= 0) {
        versions[existingIndex] = newVersion;
      } else {
        versions.push(newVersion);
      }

      localStorage.setItem(this.GRID_STATE_VERSION_KEY, JSON.stringify(versions));
    } catch (error) {
      console.error("Error setting grid version:", error);
    }
  }
}
